// src/app/push-notification.service.ts
import { Injectable, inject } from "@angular/core";
import { BackendService } from "./backend-api.service";
import { environment } from "src/environments/environment";

/**
 * Interfaccia per la subscription delle notifiche push
 */
export interface PushSubscriptionInfo {
  userId: string;
  tenant: string;
  subscriptionJson: string;
}

@Injectable({
  providedIn: "root"
})
export class PushNotificationService {
  private readonly vapidPublicKey = environment.vapidPublicKey;

  private backendService = inject(BackendService);
  private _isSubscribed = false;
  private _isSubscribing = false;

  /**
   * Richiede il permesso all'utente per mostrare notifiche push
   * @returns Promise che restituisce true se il permesso è concesso, false altrimenti
   * @example
   * ```typescript
   * const hasPermission = await pushService.requestPermission();
   * if (hasPermission) {
   *   console.log('Permesso notifiche concesso');
   * }
   * ```
   */
  async requestPermission(): Promise<boolean> {
    if (!("Notification" in window)) {
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      return permission === "granted";
    } catch (error) {
      return false;
    }
  }

  /**
   * Sottoscrive l'utente alle notifiche push
   * @param userId - ID univoco dell'utente
   * @param tenant - Nome del tenant
   * @returns Promise di completamento della sottoscrizione
   * @throws {Error} Se il browser non supporta i service worker o la sottoscrizione fallisce
   */
  async subscribeToNotifications(userId: string, tenant: string): Promise<void> {
    if (this._isSubscribing) {
      return;
    }

    this._isSubscribing = true;
    
    try {
      // Validazione parametri
      if (!userId?.trim()) {
        throw new Error("User ID è richiesto per la sottoscrizione alle notifiche");
      }

      if (!tenant?.trim()) {
        throw new Error("Tenant è richiesto per la sottoscrizione alle notifiche");
      }

      if (!("serviceWorker" in navigator)) {
        throw new Error("Service workers non sono supportati in questo browser");
      }

      if (!("PushManager" in window)) {
        throw new Error("Push notifications non sono supportate in questo browser");
      }

      // Use the existing Angular-managed service worker registration
      // instead of registering again (which causes conflicts)
      let registration: ServiceWorkerRegistration;
      
      try {
        // Wait for the Angular-managed service worker to be ready (with timeout)
        registration = await Promise.race([
          navigator.serviceWorker.ready,
          new Promise<never>((_, reject) => 
            setTimeout(() => reject(new Error("Service worker ready timeout")), 15000)
          )
        ]);
      } catch (error) {
        throw new Error("Service worker non è pronto per le notifiche push");
      }

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKey) as BufferSource
      });

      const userAgent = navigator.userAgent;

      // Invia la subscription al backend con typing corretto
      await (this.backendService.subscribeToNotifications as (
        userId: string,
        tenant: string,
        subscription: string,
        userAgent: string
      ) => Promise<void>)(
        userId,
        tenant,
        JSON.stringify(subscription.toJSON()),
        userAgent
      );

      this._isSubscribed = true;

    } catch (error) {
      if (error instanceof Error) {
        // Fornisci messaggi di errore più specifici
        if (error.name === "NotAllowedError") {
          throw new Error("Permesso per le notifiche negato dall'utente");
        } else if (error.name === "NotSupportedError") {
          throw new Error("Push notifications non supportate su questo dispositivo");
        } else if (error.message.includes("manifest")) {
          throw new Error("Configurazione manifest PWA non valida");
        }
      }
      throw new Error("Errore durante la sottoscrizione alle notifiche push");
    } finally {
      this._isSubscribing = false;
    }
  }

  /**
   * Rimuove la sottoscrizione alle notifiche push
   * @returns Promise di completamento della rimozione
   * @throws {Error} Se la rimozione della sottoscrizione fallisce
   */
  async unsubscribeFromNotifications(): Promise<void> {
    try {
      const registration = await navigator.serviceWorker.getRegistration();

      if (!registration) {
        this._isSubscribed = false;
        return;
      }

      const subscription = await registration.pushManager.getSubscription();

      if (!subscription) {
        this._isSubscribed = false;
        return;
      }

      // Rimuovi la sottoscrizione lato client
      const unsubscribeSuccess = await subscription.unsubscribe();

      if (!unsubscribeSuccess) {
        // Silent handling
      }

      // Notifica il backend della cancellazione con typing corretto
      await (this.backendService.unsubscribeFromNotifications as (
        endpoint: string
      ) => Promise<void>)(subscription.endpoint);

      this._isSubscribed = false;

    } catch (error) {
      // Imposta comunque come non sottoscritto in caso di errore
      this._isSubscribed = false;

      throw new Error("Errore durante la rimozione della sottoscrizione alle notifiche");
    }
  }

  /**
   * Verifica se l'utente è attualmente sottoscritto alle notifiche push
   * @returns True se l'utente è sottoscritto, false altrimenti
   */
  get isSubscribed(): boolean {
    return this._isSubscribed;
  }

  /**
   * Verifica se il browser supporta le notifiche push
   * @returns True se le notifiche push sono supportate, false altrimenti
   */
  isPushNotificationSupported(): boolean {
    return (
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window
    );
  }

  /**
   * Ottiene lo stato corrente della sottoscrizione push
   * @returns Promise con la sottoscrizione attiva o null se non sottoscritto
   */
  async getCurrentSubscription(): Promise<PushSubscription | null> {
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        return null;
      }

      return await registration.pushManager.getSubscription();
    } catch (error) {
      return null;
    }
  }

  /**
   * Invia un messaggio al service worker per testare la comunicazione
   * @returns Promise che conferma se il service worker è attivo e risponde
   */
  async testServiceWorkerCommunication(): Promise<boolean> {
    try {
      if (!("serviceWorker" in navigator)) {
        return false;
      }

      const registration = await navigator.serviceWorker.ready;

      return await new Promise<boolean>((resolve) => {
        const messageChannel = new MessageChannel();

        messageChannel.port1.onmessage = (event: MessageEvent) => {
          const data = event.data as { type?: string };
          if (data?.type === "PONG") {
            resolve(true);
          } else {
            resolve(false);
          }
        };

        // Timeout after 5 seconds
        setTimeout(() => resolve(false), 5000);

        // Send ping message
        registration.active?.postMessage(
          { type: "PING" },
          [messageChannel.port2]
        );
      });
    } catch (error) {
      return false;
    }
  }

  /**
   * Converte una stringa VAPID base64 in Uint8Array per la sottoscrizione push
   * @param base64String - Chiave VAPID in formato base64
   * @returns Array di byte per la sottoscrizione push
   */
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = "=".repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, "+")
      .replace(/_/g, "/");

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}
